import { startTransition, useEffect, useMemo, useState } from "react"

import {
  PublicLookupSectionSkeleton,
  PublicSidebarSectionSkeleton,
} from "@/components/app/public-loading-skeleton"
import { Button } from "@/components/ui/button"
import {
  buildPublicIncidentPayload,
  buildTemplatePublicIncidentPayload,
  createInitialReportForm,
  createInitialTemplateReportForm,
  isAllowedIncidentPhoto,
  normalizeTemplateFieldValue,
  validateTemplateIncidentForm,
  validateIncidentForm,
  type BehaviorType,
  type IncidentType,
  type ReportFormState,
  type ReportGpsCoordinates,
  type Severity,
  type TemplateReportFieldValue,
  type TemplateReportFormState,
} from "@/features/public/report/incident-payload"
import { uploadIncidentEvidence } from "@/features/public/report/uploadthing-client"
import {
  getMaskedPlate,
  getReportSubjectSummary,
  getVehicleLabel,
  type PublicScanSearch,
} from "@/features/public/shared/scan"
import {
  apiClient,
  getApiFailure,
  isApiError,
  type PublicIncidentResponse,
  type PublicLookupData,
} from "@/lib/api/client"
import {
  getPlantationLocalLanguageOption,
  getInitialSafetyQrReportingTemplateAction,
  getSafetyQrReportingTemplateAction,
  localizeReportingTemplateForLookup,
  resolveSafetyQrReportingTemplateForLookup,
  resolvePlantationPreferredLocalLanguageCode,
  type SafetyQrReportingTemplateActionDefinition,
  type SafetyQrReportingFieldDefinition,
  type SafetyQrReportingTemplateDefinition,
} from "@/lib/safetyqr-reporting-templates"

type LookupStatus = "idle" | "loading" | "ready" | "warning"
type GpsStatus = "disabled" | "error" | "loading" | "ready"
type DriverLanguage = "en" | "hi" | "ar"

interface PublicReportPageProps {
  initialSearch?: PublicScanSearch
  initialTag?: string | null
}

interface TemplateDraftState {
  form: TemplateReportFormState
  sourceKey: string
}

interface UploadIncidentEvidenceResult {
  uploadedPhotos: Awaited<ReturnType<typeof uploadIncidentEvidence>>["uploadedPhotos"]
  uploadedVoiceNotes: Awaited<ReturnType<typeof uploadIncidentEvidence>>["uploadedVoiceNotes"]
}

const SECTION_CLASS = "shell-panel rounded-4xl border border-border/70 p-5 sm:p-6"
const INPUT_CLASS =
  "mt-2 w-full rounded-2xl border border-border bg-background/90 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15"
const TEXTAREA_CLASS = `${INPUT_CLASS} min-h-32 resize-y`
const LABEL_CLASS = "text-sm font-medium text-foreground"
const HELP_CLASS = "mt-2 text-xs leading-5 text-muted-foreground"
const BANNER_BASE = "rounded-3xl border px-4 py-3 text-sm leading-6"
const ALLOWED_VOICE_NOTE_TYPES = new Set([
  "audio/mpeg",
  "audio/mp3",
  "audio/mp4",
  "audio/m4a",
  "audio/x-m4a",
  "audio/x-mp4a",
  "audio/mp4a-latm",
  "audio/aac",
  "audio/webm",
  "audio/wav",
  "audio/x-wav",
  "audio/ogg",
  "audio/mpga",
  "audio/3gp",
  "audio/3gpp",
])
const VOICE_NOTE_EXTENSION_PATTERN = /\.(mp3|mp4|m4a|aac|wav|webm|ogg|mpga|3gp|3gpp)$/i
const DRIVER_LANGUAGE_OPTIONS: Array<{
  helper: string
  label: string
  nativeLabel: string
  value: DriverLanguage
}> = [
  {
    value: "en",
    label: "English",
    nativeLabel: "English",
    helper: "English only",
  },
  {
    value: "hi",
    label: "Hindi",
    nativeLabel: "हिन्दी",
    helper: "English + हिन्दी",
  },
  {
    value: "ar",
    label: "Arabic",
    nativeLabel: "العربية",
    helper: "English + العربية",
  },
]
const BILINGUAL_LABELS: Record<string, Record<Exclude<DriverLanguage, "en">, string>> = {
  chooseLanguage: {
    hi: "भाषा चुनें",
    ar: "اختر اللغة",
  },
  safetyQrTag: {
    hi: "सेफ्टीक्यूआर टैग",
    ar: "ملصق SafetyQR",
  },
  reportPath: {
    hi: "रिपोर्ट पथ",
    ar: "مسار البلاغ",
  },
  severity: {
    hi: "गंभीरता",
    ar: "الخطورة",
  },
  incidentPhotos: {
    hi: "घटना की तस्वीरें",
    ar: "صور الحادث",
  },
  voiceNote: {
    hi: "वॉइस नोट",
    ar: "ملاحظة صوتية",
  },
  yourName: {
    hi: "आपका नाम",
    ar: "اسمك",
  },
  yourPhone: {
    hi: "आपका फ़ोन",
    ar: "رقم هاتفك",
  },
  yourEmail: {
    hi: "आपका ईमेल",
    ar: "بريدك الإلكتروني",
  },
  locationDetails: {
    hi: "स्थान का विवरण",
    ar: "تفاصيل الموقع",
  },
  sceneDetails: {
    hi: "घटना का विवरण",
    ar: "تفاصيل الواقعة",
  },
  vehicleReference: {
    hi: "वाहन संदर्भ",
    ar: "مرجع المركبة",
  },
  gpsStatus: {
    hi: "जीपीएस स्थिति",
    ar: "حالة GPS",
  },
  checkTag: {
    hi: "टैग जांचें",
    ar: "تحقق من الوسم",
  },
  submitReport: {
    hi: "रिपोर्ट सबमिट करें",
    ar: "إرسال البلاغ",
  },
}

const REPORT_INCIDENT_TYPES: Array<{ label: string; value: IncidentType }> = [
  { label: "Accident", value: "accident" },
  { label: "Medical issue", value: "medical" },
  { label: "Fire or smoke", value: "fire" },
  { label: "Breakdown", value: "breakdown" },
  { label: "Theft or tampering", value: "theft" },
  { label: "Hazard nearby", value: "hazard" },
]

const REPORT_BEHAVIOR_TYPES: Array<{ label: string; value: BehaviorType }> = [
  { label: "Unsafe driving", value: "unsafe_driving" },
  {
    label: "Wrong-way or lane violation",
    value: "wrong_way_or_lane_violation",
  },
  {
    label: "Unsafe parking or blocking",
    value: "unsafe_parking_or_blocking",
  },
  { label: "Phone use or distraction", value: "phone_use_or_distraction" },
  { label: "Road rage or abuse", value: "road_rage_or_abuse" },
  { label: "Vehicle condition issue", value: "vehicle_condition_issue" },
  {
    label: "Tampering or theft attempt",
    value: "tampering_or_theft_attempt",
  },
  { label: "Other unsafe behavior", value: "other_behavior" },
]

const REPORT_SEVERITIES: Array<{ label: string; value: Severity }> = [
  { label: "Minor", value: "minor" },
  { label: "Moderate", value: "moderate" },
  { label: "Severe", value: "severe" },
  { label: "Critical", value: "critical" },
]

function seedTemplateForm(
  previous: TemplateReportFormState | null,
  action: SafetyQrReportingTemplateActionDefinition,
  template: SafetyQrReportingTemplateDefinition,
  deviceTag: string,
  defaults: {
    locationDescription?: string
    plantationReference?: string
    siteContext?: string
    vehicleReference?: string
  }
): TemplateReportFormState {
  const nextForm = createInitialTemplateReportForm(deviceTag, template, action)
  const priorValues = previous?.values ?? {}

  return {
    deviceTag,
    values: Object.fromEntries(
      template.fields.map((field) => {
        const previousValue = priorValues[field.id]

        if (previousValue !== undefined) {
          return [field.id, previousValue]
        }

        if (field.id === "vehicleReference" && defaults.vehicleReference) {
          return [field.id, defaults.vehicleReference]
        }

        if (field.id === "locationDescription" && defaults.locationDescription) {
          return [field.id, defaults.locationDescription]
        }

        if (field.id === "plantationReference" && defaults.plantationReference) {
          return [field.id, defaults.plantationReference]
        }

        if (field.id === "siteContext" && defaults.siteContext) {
          return [field.id, defaults.siteContext]
        }

        return [field.id, nextForm.values[field.id]]
      })
    ),
  }
}

function bilingualLabel(
  english: string,
  key: keyof typeof BILINGUAL_LABELS,
  language: DriverLanguage | null
) {
  if (!language || language === "en") {
    return english
  }

  const translated = BILINGUAL_LABELS[key][language]
  return translated ? `${english} / ${translated}` : english
}

const TEMPLATE_COPY_TRANSLATIONS: Record<
  string,
  Record<Exclude<DriverLanguage, "en">, string>
> = {
  Severity: { hi: "गंभीरता", ar: "الخطورة" },
  "Incident type": { hi: "घटना का प्रकार", ar: "نوع الحادث" },
  "Injury status": { hi: "चोट की स्थिति", ar: "حالة الإصابة" },
  "People involved": { hi: "शामिल लोग", ar: "الأشخاص المشاركون" },
  "Estimated delay": { hi: "अनुमानित देरी", ar: "التأخير المتوقع" },
  "Your name": { hi: "आपका नाम", ar: "اسمك" },
  "Your phone": { hi: "आपका फ़ोन", ar: "رقم هاتفك" },
  "Your email": { hi: "आपका ईमेल", ar: "بريدك الإلكتروني" },
  "Location details": { hi: "स्थान का विवरण", ar: "تفاصيل الموقع" },
  "Scene details": { hi: "घटना का विवरण", ar: "تفاصيل الواقعة" },
  "Vehicle reference": { hi: "वाहन संदर्भ", ar: "مرجع المركبة" },
  "Police help is needed": { hi: "पुलिस सहायता चाहिए", ar: "مطلوب مساعدة الشرطة" },
  "Ambulance help is needed": {
    hi: "एम्बुलेंस सहायता चाहिए",
    ar: "مطلوب مساعدة الإسعاف",
  },
  Accident: { hi: "दुर्घटना", ar: "حادث" },
  "Medical issue": { hi: "चिकित्सीय समस्या", ar: "مشكلة طبية" },
  "Fire or smoke": { hi: "आग या धुआं", ar: "حريق أو دخان" },
  Breakdown: { hi: "खराबी", ar: "عطل" },
  "Theft or tampering": { hi: "चोरी या छेड़छाड़", ar: "سرقة أو عبث" },
  "Hazard nearby": { hi: "पास में खतरा", ar: "خطر قريب" },
  "No known injuries": { hi: "कोई ज्ञात चोट नहीं", ar: "لا توجد إصابات معروفة" },
  "Possible injuries": { hi: "संभावित चोटें", ar: "إصابات محتملة" },
  "Serious injuries": { hi: "गंभीर चोटें", ar: "إصابات خطيرة" },
  Unknown: { hi: "अज्ञात", ar: "غير معروف" },
  Select: { hi: "चुनें", ar: "اختر" },
  "Select if known": { hi: "पता हो तो चुनें", ar: "اختر إذا كان معروفا" },
  "Responders can use this if they need a quick callback.": {
    hi: "जरूरत पड़ने पर रिस्पॉन्डर इस नंबर पर जल्दी कॉल बैक कर सकते हैं।",
    ar: "يمكن لفرق الاستجابة استخدام هذا الرقم إذا احتاجوا معاودة اتصال سريعة.",
  },
  "Share the road, landmark, lane, or nearby exit if GPS is limited.": {
    hi: "यदि GPS सीमित हो तो सड़क, लैंडमार्क, लेन या पास का एग्ज़िट लिखें।",
    ar: "شارك الطريق أو المعلم أو المسار أو المخرج القريب إذا كان GPS محدودا.",
  },
  "Store this escalation hint with the report even before authority routing is expanded.": {
    hi: "अथॉरिटी रूटिंग पूरी तरह विस्तृत होने से पहले भी इस एस्केलेशन संकेत को रिपोर्ट में सहेजें।",
    ar: "احفظ إشارة التصعيد هذه مع البلاغ حتى قبل توسيع توجيه الجهات المختصة.",
  },
}

const TEMPLATE_FIELD_TRANSLATIONS: Record<
  string,
  Record<Exclude<DriverLanguage, "en">, string>
> = {
  severity: { hi: "गंभीरता", ar: "الخطورة" },
  incidentType: { hi: "घटना का प्रकार", ar: "نوع الحادث" },
  injuryStatus: { hi: "चोट की स्थिति", ar: "حالة الإصابة" },
  peopleInvolved: { hi: "शामिल लोग", ar: "الأشخاص المشاركون" },
  delay: { hi: "अनुमानित देरी", ar: "التأخير المتوقع" },
  reporterName: { hi: "आपका नाम", ar: "اسمك" },
  reporterPhone: { hi: "आपका फ़ोन", ar: "رقم هاتفك" },
  reporterEmail: { hi: "आपका ईमेल", ar: "بريدك الإلكتروني" },
  locationDescription: { hi: "स्थान का विवरण", ar: "تفاصيل الموقع" },
  vehicleReference: { hi: "वाहन संदर्भ", ar: "مرجع المركبة" },
  description: { hi: "घटना का विवरण", ar: "تفاصيل الواقعة" },
  policeAssistanceRequested: { hi: "पुलिस सहायता चाहिए", ar: "مطلوب مساعدة الشرطة" },
  ambulanceRequested: { hi: "एम्बुलेंस सहायता चाहिए", ar: "مطلوب مساعدة الإسعاف" },
}

const TEMPLATE_OPTION_TRANSLATIONS: Record<
  string,
  Record<Exclude<DriverLanguage, "en">, string>
> = {
  "severity:minor": { hi: "मामूली", ar: "طفيف" },
  "severity:moderate": { hi: "मध्यम", ar: "متوسط" },
  "severity:severe": { hi: "गंभीर", ar: "شديد" },
  "severity:critical": { hi: "अत्यंत गंभीर", ar: "حرج" },
  "incidentType:accident": { hi: "दुर्घटना", ar: "حادث" },
  "incidentType:medical": { hi: "चिकित्सीय समस्या", ar: "مشكلة طبية" },
  "incidentType:fire": { hi: "आग या धुआं", ar: "حريق أو دخان" },
  "incidentType:breakdown": { hi: "खराबी", ar: "عطل" },
  "incidentType:theft": { hi: "चोरी या छेड़छाड़", ar: "سرقة أو عبث" },
  "incidentType:hazard": { hi: "पास में खतरा", ar: "خطر قريب" },
  "injuryStatus:none-known": { hi: "कोई ज्ञात चोट नहीं", ar: "لا توجد إصابات معروفة" },
  "injuryStatus:possible": { hi: "संभावित चोटें", ar: "إصابات محتملة" },
  "injuryStatus:serious": { hi: "गंभीर चोटें", ar: "إصابات خطيرة" },
  "injuryStatus:unknown": { hi: "अज्ञात", ar: "غير معروف" },
  "peopleInvolved:": { hi: "चुनें", ar: "اختر" },
  "delay:": { hi: "पता हो तो चुनें", ar: "اختر إذا كان معروفا" },
}

const TEMPLATE_HELPER_TRANSLATIONS: Record<
  string,
  Record<Exclude<DriverLanguage, "en">, string>
> = {
  reporterPhone: {
    hi: "जरूरत पड़ने पर रिस्पॉन्डर इस नंबर पर जल्दी कॉल बैक कर सकते हैं।",
    ar: "يمكن لفرق الاستجابة استخدام هذا الرقم إذا احتاجوا معاودة اتصال سريعة.",
  },
  locationDescription: {
    hi: "यदि GPS सीमित हो तो सड़क, लैंडमार्क, लेन या पास का एग्ज़िट लिखें।",
    ar: "شارك الطريق أو المعلم أو المسار أو المخرج القريب إذا كان GPS محدودا.",
  },
  policeAssistanceRequested: {
    hi: "अथॉरिटी रूटिंग पूरी तरह विस्तृत होने से पहले भी इस एस्केलेशन संकेत को रिपोर्ट में सहेजें।",
    ar: "احفظ إشارة التصعيد هذه مع البلاغ حتى قبل توسيع توجيه الجهات المختصة.",
  },
}

function bilingualTemplateCopy(text: string, language: DriverLanguage | null) {
  if (!language || language === "en") {
    return text
  }

  const translated = TEMPLATE_COPY_TRANSLATIONS[text]?.[language]
  return translated ? `${text} / ${translated}` : text
}

function bilingualTemplateFieldLabel(
  field: SafetyQrReportingFieldDefinition,
  language: DriverLanguage | null
) {
  if (!language || language === "en") {
    return field.label
  }

  const fieldTranslation = TEMPLATE_FIELD_TRANSLATIONS[field.id]?.[language]
  return fieldTranslation
    ? `${field.label} / ${fieldTranslation}`
    : bilingualTemplateCopy(field.label, language)
}

function bilingualTemplateHelperText(
  field: SafetyQrReportingFieldDefinition,
  language: DriverLanguage | null
) {
  if (!field.helperText || !language || language === "en") {
    return field.helperText ?? null
  }

  const fieldTranslation = TEMPLATE_HELPER_TRANSLATIONS[field.id]?.[language]
  return fieldTranslation
    ? `${field.helperText} / ${fieldTranslation}`
    : bilingualTemplateCopy(field.helperText, language)
}

function bilingualTemplateOptionLabel(
  field: SafetyQrReportingFieldDefinition,
  option: { label: string; value: string },
  language: DriverLanguage | null
) {
  if (!language || language === "en") {
    return option.label
  }

  const optionTranslation =
    TEMPLATE_OPTION_TRANSLATIONS[`${field.id}:${option.value}`]?.[language]

  return optionTranslation
    ? `${option.label} / ${optionTranslation}`
    : bilingualTemplateCopy(option.label, language)
}

function getCombinedWarning(parts: Array<string | null | undefined>) {
  const normalized = parts
    .map((part) => part?.trim())
    .filter(Boolean) as string[]

  if (normalized.length === 0) {
    return null
  }

  return normalized.join(" ")
}

export function PublicReportPage({
  initialTag = null,
}: PublicReportPageProps) {
  const [form, setForm] = useState<ReportFormState>(() =>
    createInitialReportForm(initialTag ?? "")
  )
  const [bootstrapLookupPending, setBootstrapLookupPending] = useState(
    () => Boolean(initialTag)
  )
  const [templateDraft, setTemplateDraft] = useState<TemplateDraftState | null>(
    null
  )
  const [selectedTemplateActionId, setSelectedTemplateActionId] = useState<string | null>(null)
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>(
    initialTag ? "loading" : "idle"
  )
  const [preferredLanguage, setPreferredLanguage] = useState<DriverLanguage | null>(
    null
  )
  const [lookupMessage, setLookupMessage] = useState<string>("")
  const [lookupData, setLookupData] = useState<PublicLookupData | null>(null)
  const [tagLocked, setTagLocked] = useState(Boolean(initialTag))
  const [formError, setFormError] = useState<string>("")
  const [gpsStatus, setGpsStatus] = useState<GpsStatus>("loading")
  const [gpsMessage, setGpsMessage] = useState(
    "Allow location access to attach GPS coordinates to the incident."
  )
  const [gpsCoordinates, setGpsCoordinates] =
    useState<ReportGpsCoordinates | null>(null)
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([])
  const [selectedVoiceNote, setSelectedVoiceNote] = useState<File | null>(null)
  const [submitStatus, setSubmitStatus] = useState<"idle" | "submitting">("idle")
  const [submitMessage, setSubmitMessage] = useState("Submit report")
  const [submittedIncident, setSubmittedIncident] =
    useState<PublicIncidentResponse | null>(null)
  const [submitWarning, setSubmitWarning] = useState<string | null>(null)
  const [templateConfirmationVisible, setTemplateConfirmationVisible] =
    useState(false)
  const [zoneNameDraft, setZoneNameDraft] = useState("")
  const [zoneSubmitStatus, setZoneSubmitStatus] = useState<"idle" | "saving">("idle")

  async function runLookup(tag: string, options?: { preserveLoadingState?: boolean }) {
    const trimmedTag = tag.trim()

    if (!trimmedTag) {
      setFormError("Enter the SafetyQR tag before checking it.")
      return
    }

    if (!options?.preserveLoadingState) {
      setLookupStatus("loading")
      setLookupMessage("")
    }
    setFormError("")

    try {
      const lookup = await apiClient.lookupPublicTag({
        tag: trimmedTag,
      })
      const registrationRedirectRequired =
        Boolean(lookup.data.kit?.id)
        && lookup.data.scanDestination === "register"
      const plantationsActivationRequired =
        lookup.data.kit?.productFamily === "site_safety_qr"
        && lookup.data.kit?.solutionCategory === "plantations"
        && lookup.data.activationState !== "activated"

      startTransition(() => {
        setLookupData(lookup.data)
        setLookupStatus(
          registrationRedirectRequired ? "warning" : "ready"
        )
        setLookupMessage(
          plantationsActivationRequired
            ? "This Plantation tag still needs first-scan activation. Complete registration first before Panic Alert or Report Incident can continue."
            : registrationRedirectRequired
              ? "This SafetyQR kit still needs first-scan registration. Complete registration once, and every sibling sticker will open reporting automatically after activation."
            : "SafetyQR context loaded successfully."
        )
        setForm((currentForm) => ({
          ...currentForm,
          deviceTag: lookup.data.tag,
          vehicleReference:
            currentForm.vehicleReference || getVehicleLabel(lookup.data.vehicle),
        }))
        setZoneNameDraft(lookup.data.physicalTag?.zoneName ?? "")
        setTemplateConfirmationVisible(false)
      })
    } catch (error) {
      if (isApiError(error)) {
        const failure = getApiFailure(error)

        if (failure?.registrationRequired) {
          startTransition(() => {
            setLookupData(null)
            setLookupStatus("warning")
            setLookupMessage(
              "This tag is not registered yet. Public reporting can still continue with the scanned code."
            )
            setTagLocked(false)
            setTemplateDraft(null)
            setZoneNameDraft("")
            setTemplateConfirmationVisible(false)
          })
          return
        }
      }

      setLookupStatus("warning")
      setLookupMessage(
        error instanceof Error
          ? error.message
          : "The scanned tag could not be confirmed right now. You can still continue with the code you entered."
      )
      setTagLocked(false)
      setTemplateDraft(null)
      setZoneNameDraft("")
      setTemplateConfirmationVisible(false)
    }
  }

  function requestGpsLocation(options?: { preserveLoadingState?: boolean }) {
    if (!navigator.geolocation) {
      setGpsStatus("disabled")
      setGpsMessage(
        "This browser does not expose geolocation. You can still submit the incident without GPS."
      )
      return
    }

    if (!options?.preserveLoadingState) {
      setGpsStatus("loading")
      setGpsMessage("Checking your location...")
    }

    const handleSuccess = (position: GeolocationPosition) => {
      startTransition(() => {
        setGpsCoordinates({
          accuracy: position.coords.accuracy,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
        setGpsStatus("ready")
        setGpsMessage(
          `GPS captured at roughly ${Math.round(position.coords.accuracy)} meters accuracy.`
        )
      })
    }

    const handleFailure = (error: GeolocationPositionError) => {
      const message =
        error.code === error.PERMISSION_DENIED
          ? "Location permission was denied. You can retry or continue without GPS."
          : error.code === error.POSITION_UNAVAILABLE
            ? "Your device could not resolve its location right now."
            : "The location request timed out."

      setGpsStatus("error")
      setGpsMessage(message)
    }

    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      (error) => {
        if (error.code !== error.TIMEOUT) {
          handleFailure(error)
          return
        }

        navigator.geolocation.getCurrentPosition(
          handleSuccess,
          handleFailure,
          {
            enableHighAccuracy: false,
            maximumAge: 5 * 60_000,
            timeout: 5_000,
          }
        )
      },
      {
        enableHighAccuracy: false,
        maximumAge: 120_000,
        timeout: 10_000,
      }
    )
  }

  useEffect(() => {
    queueMicrotask(() => {
      requestGpsLocation({ preserveLoadingState: true })
    })
  }, [])

  useEffect(() => {
    if (!initialTag) {
      return
    }

    queueMicrotask(() => {
      void runLookup(initialTag, { preserveLoadingState: true }).finally(() => {
        setBootstrapLookupPending(false)
      })
    })
  }, [initialTag])

  const isPlantationsLookup = useMemo(
    () =>
      lookupData?.kit?.productFamily === "site_safety_qr"
      && lookupData?.kit?.solutionCategory === "plantations",
    [lookupData]
  )
  const plantationsActivationRequired = useMemo(
    () =>
      isPlantationsLookup
      && lookupData?.activationState !== "activated",
    [isPlantationsLookup, lookupData]
  )
  const plantationsZoneRequired = useMemo(
    () =>
      isPlantationsLookup
      && lookupData?.activationState === "activated"
      && lookupData?.physicalTag?.zoneNamingState === "required",
    [isPlantationsLookup, lookupData]
  )
  const registrationRedirectRequired = useMemo(
    () =>
      Boolean(lookupData?.kit?.id)
      && lookupData?.scanDestination === "register"
      && !isPlantationsLookup,
    [isPlantationsLookup, lookupData]
  )
  const reportingUiBlocked =
    registrationRedirectRequired || plantationsActivationRequired || plantationsZoneRequired
  const plantationsLocalLanguageCode = useMemo(
    () => resolvePlantationPreferredLocalLanguageCode(lookupData),
    [lookupData]
  )
  const plantationsLocalLanguageLabel = useMemo(
    () =>
      getPlantationLocalLanguageOption(plantationsLocalLanguageCode)?.label ?? null,
    [plantationsLocalLanguageCode]
  )
  const reportingTemplate = useMemo(
    () => {
      if (registrationRedirectRequired || plantationsActivationRequired) {
        return null
      }

      const baseTemplate = resolveSafetyQrReportingTemplateForLookup(lookupData)
      return localizeReportingTemplateForLookup(baseTemplate, lookupData)
    },
    [lookupData, plantationsActivationRequired, registrationRedirectRequired]
  )
  const selectedTemplateAction = useMemo(
    () =>
      reportingTemplate
        ? getSafetyQrReportingTemplateAction(
            reportingTemplate,
            selectedTemplateActionId
          )
        : null,
    [reportingTemplate, selectedTemplateActionId]
  )
  const resolvedTemplateAction = useMemo(
    () => {
      if (selectedTemplateAction) {
        return selectedTemplateAction
      }

      if (!reportingTemplate) {
        return null
      }

      if (reportingTemplate.id === "safetyqr.report.plantations.v1") {
        return null
      }

      return getInitialSafetyQrReportingTemplateAction(reportingTemplate)
    },
    [reportingTemplate, selectedTemplateAction]
  )
  const isPlantationsTemplate = reportingTemplate?.id === "safetyqr.report.plantations.v1"
  const subjectSummary = useMemo(
    () =>
      reportingTemplate?.subjectSummary ??
      getReportSubjectSummary(form.reportKind, lookupData?.tagType ?? null),
    [form.reportKind, lookupData, reportingTemplate]
  )
  const resolvedVehicleLabel = useMemo(
    () => getVehicleLabel(lookupData?.vehicle ?? null),
    [lookupData]
  )
  const resolvedPlantationReference = useMemo(() => {
    if (!isPlantationsLookup) {
      return ""
    }

    const pieces = [
      lookupData?.plantation?.plantationId,
      lookupData?.kit?.code,
      lookupData?.tag,
    ].filter(Boolean)

    return pieces.join(" / ")
  }, [isPlantationsLookup, lookupData])
  const resolvedSiteContext = useMemo(() => {
    if (!isPlantationsLookup) {
      return ""
    }

    return lookupData?.plantation?.plantationName ?? ""
  }, [isPlantationsLookup, lookupData])
  const resolvedZoneName = useMemo(
    () => lookupData?.physicalTag?.zoneName ?? lookupData?.plantation?.zoneName ?? "",
    [lookupData]
  )
  const templateSeedDefaults = useMemo(
    () => ({
      locationDescription: resolvedZoneName,
      plantationReference: resolvedPlantationReference,
      siteContext: resolvedSiteContext,
      vehicleReference: resolvedVehicleLabel,
    }),
    [
      resolvedPlantationReference,
      resolvedSiteContext,
      resolvedVehicleLabel,
      resolvedZoneName,
    ]
  )
  const maskedPlate = useMemo(
    () => getMaskedPlate(lookupData?.vehicle ?? null),
    [lookupData]
  )
  const isVehicleOrFleetTagFlow = useMemo(
    () =>
      lookupData?.tagType === "fleet_tag" || lookupData?.tagType === "vehicle_sticker",
    [lookupData]
  )
  const resolvedTemplateTag = useMemo(
    () => form.deviceTag || initialTag || lookupData?.tag || "",
    [form.deviceTag, initialTag, lookupData?.tag]
  )
  const templateSourceKey = useMemo(
    () =>
      reportingTemplate && resolvedTemplateAction
        ? `${reportingTemplate.id}:${resolvedTemplateAction.id}:${resolvedTemplateTag}`
        : null,
    [reportingTemplate, resolvedTemplateAction, resolvedTemplateTag]
  )
  const templateForm = useMemo(() => {
    if (!reportingTemplate || !resolvedTemplateAction || !templateSourceKey) {
      return null
    }

    const previousForm =
      templateDraft?.sourceKey === templateSourceKey ? templateDraft.form : null

    return seedTemplateForm(
      previousForm,
      resolvedTemplateAction,
      reportingTemplate,
      resolvedTemplateTag,
      templateSeedDefaults
    )
  }, [
    reportingTemplate,
    resolvedTemplateAction,
    resolvedTemplateTag,
    templateSeedDefaults,
    templateDraft,
    templateSourceKey,
  ])
  const visibleTemplateFields = useMemo(() => {
    if (!reportingTemplate || !resolvedTemplateAction) {
      return []
    }

    const allowedFieldIds = new Set(resolvedTemplateAction.fieldIds)
    return reportingTemplate.fields.filter((field) => allowedFieldIds.has(field.id))
  }, [reportingTemplate, resolvedTemplateAction])
  const resolvedTemplateHeadline = useMemo(
    () => resolvedTemplateAction?.headline ?? reportingTemplate?.headline ?? null,
    [reportingTemplate, resolvedTemplateAction]
  )
  const resolvedTemplateHelperText = useMemo(
    () => resolvedTemplateAction?.helperText ?? reportingTemplate?.helperText ?? null,
    [reportingTemplate, resolvedTemplateAction]
  )
  const resolvedTemplateConfirmation = useMemo(
    () => resolvedTemplateAction?.confirmation ?? reportingTemplate?.confirmation ?? null,
    [reportingTemplate, resolvedTemplateAction]
  )
  const resolvedTemplateSubmitLabel = useMemo(() => {
    if (!resolvedTemplateAction) {
      return "Submit report"
    }

    if (
      reportingTemplate?.id === "safetyqr.report.vehicle_incident.v1"
      && resolvedTemplateAction.id === "report_incident"
    ) {
      return "Submit report"
    }

    if (
      isPlantationsTemplate
      && resolvedTemplateAction.id === "report_incident"
    ) {
      return resolvedTemplateAction.submitLabel
    }

    return resolvedTemplateAction.submitLabel
  }, [isPlantationsTemplate, reportingTemplate?.id, resolvedTemplateAction])
  const templateSubmitsDirectly = useMemo(() => {
    if (!resolvedTemplateAction) {
      return false
    }

    if (resolvedTemplateAction.submissionMode === "direct_submit") {
      return true
    }

    return isVehicleOrFleetTagFlow && resolvedTemplateAction.id === "report_incident"
  }, [isVehicleOrFleetTagFlow, resolvedTemplateAction])
  const isBootstrapLookupPending =
    bootstrapLookupPending && lookupStatus === "loading" && !lookupData

  useEffect(() => {
    if (!reportingTemplate) {
      setSelectedTemplateActionId(null)
      return
    }

    const initialAction = getInitialSafetyQrReportingTemplateAction(reportingTemplate)

    if (
      !selectedTemplateActionId ||
      !getSafetyQrReportingTemplateAction(reportingTemplate, selectedTemplateActionId)
    ) {
      setSelectedTemplateActionId(initialAction?.id ?? null)
      setTemplateConfirmationVisible(false)
    }
  }, [reportingTemplate, selectedTemplateActionId])

  function updateForm<K extends keyof ReportFormState>(
    key: K,
    value: ReportFormState[K]
  ) {
    setForm((currentForm) => ({
      ...currentForm,
      [key]: value,
    }))
  }

  function updateTemplateForm(
    field: SafetyQrReportingFieldDefinition,
    value: TemplateReportFieldValue
  ) {
    if (!templateForm || !templateSourceKey) {
      return
    }

    const normalizedValue = normalizeTemplateFieldValue(field, value)

    setTemplateDraft((currentDraft) => {
      const baseForm =
        currentDraft?.sourceKey === templateSourceKey ? currentDraft.form : templateForm

      return {
        sourceKey: templateSourceKey,
        form: {
          ...baseForm,
          values: {
            ...baseForm.values,
            [field.id]: normalizedValue,
          },
        },
      }
    })
    setTemplateConfirmationVisible(false)
  }

  function handleTemplateActionChange(actionId: string) {
    setSelectedTemplateActionId(actionId)
    setTemplateConfirmationVisible(false)
    setTemplateDraft(null)
    setFormError("")
  }

  function handleDeviceTagChange(value: string) {
    updateForm("deviceTag", value)
    setLookupData(null)
    setLookupMessage("")
    setLookupStatus("idle")
    setTagLocked(false)
    setTemplateDraft(null)
    setZoneNameDraft("")
    setTemplateConfirmationVisible(false)
  }

  async function handleZoneSave() {
    if (!lookupData) {
      setFormError("Check the tag before naming the zone.")
      return
    }

    if (!zoneNameDraft.trim()) {
      setFormError("Enter the zone name before continuing to the report.")
      return
    }

    setFormError("")
    setZoneSubmitStatus("saving")

    try {
      const response = await apiClient.submitPublicZoneName({
        namedBy: form.reporterName.trim() || null,
        stickerId: lookupData.tag,
        tag: lookupData.tag,
        zoneName: zoneNameDraft.trim(),
      })

      startTransition(() => {
        setLookupData(response.data)
        setLookupStatus("ready")
        setLookupMessage("Zone saved. You can continue with the Plantation report.")
        setZoneNameDraft(response.data.physicalTag?.zoneName ?? zoneNameDraft.trim())
        setTemplateDraft(null)
        setTemplateConfirmationVisible(false)
      })
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "Unable to save the zone name right now."
      )
    } finally {
      setZoneSubmitStatus("idle")
    }
  }

  function handlePhotoSelection(event: React.ChangeEvent<HTMLInputElement>) {
    const incomingFiles = Array.from(event.target.files ?? [])
    const maxPhotos = reportingTemplate?.media.maxPhotos ?? 4
    const nextFiles = incomingFiles.slice(0, maxPhotos)

    for (const file of nextFiles) {
      if (!isAllowedIncidentPhoto(file)) {
        setFormError(
          "Only jpeg, png, webp, heic, and heif images can be attached to a public report."
        )
        return
      }

      if (file.size > 6 * 1024 * 1024) {
        setFormError(`${file.name} exceeds the 6 MB incident-photo limit.`)
        return
      }
    }

    const totalBytes = nextFiles.reduce((sum, file) => sum + file.size, 0)
    if (totalBytes > 24 * 1024 * 1024) {
      setFormError("The selected photos exceed the 24 MB total upload limit.")
      return
    }

    setFormError("")
    setSelectedPhotos(nextFiles)
    setTemplateConfirmationVisible(false)
  }

  function handleVoiceSelection(event: React.ChangeEvent<HTMLInputElement>) {
    const [file] = Array.from(event.target.files ?? [])

    if (!file) {
      setSelectedVoiceNote(null)
      return
    }

    const hasAllowedMimeType = Boolean(file.type) && ALLOWED_VOICE_NOTE_TYPES.has(file.type)
    const hasAllowedExtension = VOICE_NOTE_EXTENSION_PATTERN.test(file.name)

    if (!hasAllowedMimeType && !hasAllowedExtension) {
      setFormError(
        "Voice notes must be mp3, mp4, m4a, aac, wav, webm, ogg, mpga, or 3gp audio."
      )
      return
    }

    if (file.size > 24 * 1024 * 1024) {
      setFormError("Voice notes must be 24 MB or smaller.")
      return
    }

    setFormError("")
    setSelectedVoiceNote(file)
    setTemplateConfirmationVisible(false)
  }

  async function uploadAndSubmitIncident(
    buildPayload: (uploadedEvidence: Awaited<
      ReturnType<typeof uploadIncidentEvidence>
    >) => ReturnType<typeof buildPublicIncidentPayload>
  ) {
    setFormError("")
    setSubmitStatus("submitting")
    setSubmitMessage("Uploading evidence...")
    setSubmitWarning(null)

    try {
      let uploadedEvidence: UploadIncidentEvidenceResult = {
        uploadedPhotos: [],
        uploadedVoiceNotes: [],
      }
      let uploadWarning: string | null = null

      try {
        uploadedEvidence = await uploadIncidentEvidence({
          onStatusChange: setSubmitMessage,
          photos: selectedPhotos,
          voiceNote: selectedVoiceNote,
        })
      } catch (uploadError) {
        uploadWarning =
          uploadError instanceof Error
            ? uploadError.message
            : "Evidence upload failed, so this report was submitted without attachments."
        setSubmitMessage("Evidence upload failed. Submitting report...")
      }

      const payload = buildPayload(uploadedEvidence)
      const result = await apiClient.submitPublicIncident(payload)

      startTransition(() => {
        setSubmittedIncident(result.data)
        setSubmitWarning(
          getCombinedWarning([
            uploadWarning
              ? `Evidence upload issue: ${uploadWarning}`
              : null,
            result.warning ?? null,
          ])
        )
      })
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "Unable to submit the public report right now."
      )
      setSubmitStatus("idle")
      setSubmitMessage("Submit report")
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (reportingTemplate && resolvedTemplateAction && templateForm) {
      const templateErrors = validateTemplateIncidentForm(
        reportingTemplate,
        resolvedTemplateAction,
        templateForm
      )

      if (templateErrors.length > 0) {
        setFormError(templateErrors[0])
        return
      }

      setFormError("")
      if (templateSubmitsDirectly) {
        await uploadAndSubmitIncident((uploadedEvidence) =>
          buildTemplatePublicIncidentPayload({
            action: resolvedTemplateAction,
            form: templateForm,
            gpsCoordinates,
            lookup: lookupData,
            preferredLanguage,
            reportConfirmationAccepted: true,
            template: reportingTemplate,
            uploadedPhotos: uploadedEvidence.uploadedPhotos,
            uploadedVoiceNotes: uploadedEvidence.uploadedVoiceNotes,
          })
        )
        return
      }

      setTemplateConfirmationVisible(true)
      return
    }

    const errors = validateIncidentForm(form)

    if (errors.length > 0) {
      setFormError(errors[0])
      return
    }

    await uploadAndSubmitIncident((uploadedEvidence) =>
      buildPublicIncidentPayload({
        form,
        gpsCoordinates,
        lookup: lookupData,
        preferredLanguage,
        uploadedPhotos: uploadedEvidence.uploadedPhotos,
        uploadedVoiceNotes: uploadedEvidence.uploadedVoiceNotes,
      })
    )
  }

  async function handleTemplateConfirm() {
    if (!reportingTemplate || !resolvedTemplateAction || !templateForm) {
      return
    }

    await uploadAndSubmitIncident((uploadedEvidence) =>
      buildTemplatePublicIncidentPayload({
        action: resolvedTemplateAction,
        form: templateForm,
        gpsCoordinates,
        lookup: lookupData,
        preferredLanguage,
        reportConfirmationAccepted: true,
        template: reportingTemplate,
        uploadedPhotos: uploadedEvidence.uploadedPhotos,
        uploadedVoiceNotes: uploadedEvidence.uploadedVoiceNotes,
      })
    )
  }

  async function handlePlantationPanicAlert() {
    if (!reportingTemplate) {
      return
    }

    const panicAction = getSafetyQrReportingTemplateAction(reportingTemplate, "panic_alert")

    if (!panicAction) {
      return
    }

    const panicForm = seedTemplateForm(
      null,
      panicAction,
      reportingTemplate,
      resolvedTemplateTag,
      templateSeedDefaults
    )

    setSelectedTemplateActionId(panicAction.id)
    setTemplateConfirmationVisible(false)
    setFormError("")

    await uploadAndSubmitIncident((uploadedEvidence) =>
      buildTemplatePublicIncidentPayload({
        action: panicAction,
        form: panicForm,
        gpsCoordinates,
        lookup: lookupData,
        preferredLanguage,
        reportConfirmationAccepted: true,
        template: reportingTemplate,
        uploadedPhotos: uploadedEvidence.uploadedPhotos,
        uploadedVoiceNotes: uploadedEvidence.uploadedVoiceNotes,
      })
    )
  }

  if (submittedIncident) {
    return (
      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
        <div className={SECTION_CLASS}>
          <span className="text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
            Report received
          </span>
          <h2 className="mt-3 font-heading text-3xl text-balance">
            Public incident submission saved successfully.
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
            Your report has been saved successfully. Our team can now review the
            details and any attached evidence.
          </p>

          <dl className="mt-6 grid gap-4 sm:grid-cols-2">
            <SuccessStat label="Incident ID" value={String(submittedIncident.id ?? "Pending")} />
            <SuccessStat
              label="Report kind"
              value={submittedIncident.reportKind ?? form.reportKind}
            />
            <SuccessStat
              label="Subject type"
              value={submittedIncident.subjectType ?? "unknown"}
            />
            <SuccessStat
              label="Tag"
              value={submittedIncident.stickerId ?? form.deviceTag}
            />
          </dl>

          {submitWarning ? (
            <div className={`${BANNER_BASE} mt-6 border-amber-300/70 bg-amber-100/70 text-amber-950`}>
              {submitWarning}
            </div>
          ) : null}

          <div className="mt-6">
            <Button onClick={() => window.location.reload()} variant="outline">
              Submit another report
            </Button>
          </div>
        </div>

        <aside className={SECTION_CLASS}>
          <h3 className="font-semibold">What happens next</h3>
          <div className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
            <p>Keep your phone available in case responders or support staff need follow-up details.</p>
            <p>You can submit another report at any time if the situation changes.</p>
          </div>
        </aside>
      </section>
    )
  }

  return (
    <section className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(18rem,0.82fr)]">
      <form className="space-y-5" onSubmit={handleSubmit}>
        {isBootstrapLookupPending ? (
          <PublicLookupSectionSkeleton
            showStats
            testId="public-report-lookup-skeleton"
          />
        ) : (
          <section className={SECTION_CLASS}>
            <span className="text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
              Public report
            </span>
            <h2 className="mt-3 font-heading text-3xl text-balance">
              {registrationRedirectRequired ? (
                "Complete registration before reporting"
              ) : plantationsActivationRequired ? (
                "Activate this Plantation tag before reporting"
              ) : reportingTemplate ? (
                resolvedTemplateHeadline
              ) : (
                <>
                  Report an incident or unsafe behavior.
                </>
              )}
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
              {registrationRedirectRequired
                ? "The first successful scan must complete registration. After the kit is activated, every sibling sticker on that SafetyQR kit opens reporting by default."
                : plantationsActivationRequired
                ? "The first successful Plantation scan must complete registration so the kit is activated and this tag's zone can be named. Use the registration flow first, then return here for reporting."
                : reportingTemplate
                ? resolvedTemplateHelperText
                : "Use this form to look up the tag, add media, and submit an incident report."}
            </p>

            <div className="mt-6 grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
              <label className="block">
                <span className={LABEL_CLASS}>
                  {bilingualLabel("SafetyQR tag", "safetyQrTag", preferredLanguage)}
                </span>
                <input
                  className={INPUT_CLASS}
                  name="deviceTag"
                  onChange={(event) => handleDeviceTagChange(event.target.value)}
                  readOnly={tagLocked}
                  value={templateForm?.deviceTag ?? form.deviceTag}
                />
                <p className={HELP_CLASS}>
                  {tagLocked
                    ? "Captured from the scanned SafetyQR tag."
                    : "Enter or edit the visible tag code, then check the current scan state."}
                </p>
              </label>

              <div className="self-end">
                <Button
                  disabled={lookupStatus === "loading"}
                  onClick={() => void runLookup(templateForm?.deviceTag ?? form.deviceTag)}
                  type="button"
                  variant="outline"
                >
                  {bilingualLabel("Check tag", "checkTag", preferredLanguage)}
                </Button>
              </div>
            </div>

            {lookupMessage ? (
              <div
                className={`${BANNER_BASE} mt-4 ${
                  lookupStatus === "warning"
                    ? "border-amber-300/70 bg-amber-100/70 text-amber-950"
                    : "border-emerald-300/70 bg-emerald-100/70 text-emerald-950"
                }`}
              >
                {lookupMessage}
              </div>
            ) : null}

            {lookupData ? (
              <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                <InfoStat label="Tag type" value={lookupData.tagType ?? "unknown"} />
                <InfoStat
                  label="Scan destination"
                  value={lookupData.scanDestination ?? "unknown"}
                />
                {isPlantationsLookup ? (
                  <>
                    <InfoStat
                      label="Plantation"
                      value={resolvedSiteContext || "Plantation not assigned"}
                    />
                    <InfoStat
                      label="Zone"
                      value={resolvedZoneName || "Zone name required"}
                    />
                  </>
                ) : (
                  <>
                    <InfoStat
                      label={reportingTemplate ? "Template" : "Vehicle context"}
                      value={
                        reportingTemplate?.displayName ||
                        resolvedVehicleLabel ||
                        "Limited by privacy rules"
                      }
                    />
                    <InfoStat
                      label={reportingTemplate ? "Role" : "Plate suffix"}
                      value={
                        reportingTemplate
                          ? lookupData.physicalTag?.role || "Unavailable"
                          : maskedPlate || "Unavailable"
                      }
                    />
                  </>
                )}
              </dl>
            ) : null}

            {!reportingUiBlocked && !reportingTemplate ? (
              <div className="mt-5">
                <TextField
                  label={bilingualLabel(
                    "Vehicle reference",
                    "vehicleReference",
                    preferredLanguage
                  )}
                  name="vehicleReference"
                  onChange={(value) => updateForm("vehicleReference", value)}
                  type="text"
                  value={form.vehicleReference}
                />
              </div>
            ) : null}
          </section>
        )}

        {registrationRedirectRequired ? (
          <section className={SECTION_CLASS}>
            <div className={`${BANNER_BASE} mt-5 border-amber-300/70 bg-amber-100/60 text-amber-950`}>
              <strong className="block font-medium">
                Registration is required before reporting
              </strong>
              <p className="mt-2">
                This SafetyQR kit has not been activated yet. Complete first-scan
                registration once and the full kit will activate together. After that,
                every sibling sticker opens reporting automatically.
              </p>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <InfoStat
                label="Next step"
                value="Open /register for first-scan activation"
              />
              <InfoStat
                label="Current state"
                value={lookupData?.activationState ?? "registration_required"}
              />
            </div>
          </section>
        ) : plantationsActivationRequired ? (
          <section className={SECTION_CLASS}>
            <div className={`${BANNER_BASE} mt-5 border-amber-300/70 bg-amber-100/60 text-amber-950`}>
              <strong className="block font-medium">
                Activation is required before Plantation reporting
              </strong>
              <p className="mt-2">
                This Plantation tag has not been activated yet. The first successful
                scan must complete registration to activate the full kit and name
                this tag&apos;s zone before Panic Alert or Report Incident are available.
              </p>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <InfoStat
                label="Next step"
                value="Open /register for first-scan activation"
              />
              <InfoStat
                label="Current state"
                value={lookupData?.activationState ?? "registration_required"}
              />
            </div>
          </section>
        ) : plantationsZoneRequired ? (
          <section className={SECTION_CLASS}>
            <div className={`${BANNER_BASE} mt-5 border-amber-300/70 bg-amber-100/60 text-amber-950`}>
              <strong className="block font-medium">
                Name this Plantation zone before reporting
              </strong>
              <p className="mt-2">
                The kit is already activated, but this sibling tag still needs a
                write-once zone name before Panic Alert or Report Incident can continue.
              </p>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <TextField
                label="Zone name"
                name="zoneName"
                onChange={setZoneNameDraft}
                type="text"
                value={zoneNameDraft}
              />
              <label className="block">
                <span className={LABEL_CLASS}>GPS status</span>
                <div
                  className={`${BANNER_BASE} mt-2 ${
                    gpsStatus === "ready"
                      ? "border-emerald-300/70 bg-emerald-100/70 text-emerald-950"
                      : gpsStatus === "error"
                        ? "border-amber-300/70 bg-amber-100/70 text-amber-950"
                        : "border-border/70 bg-background/70"
                  }`}
                >
                  {gpsMessage}
                </div>
                <div className="mt-3">
                  <Button
                    onClick={() => requestGpsLocation()}
                    type="button"
                    variant="outline"
                  >
                    Retry GPS
                  </Button>
                </div>
              </label>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <Button
                disabled={zoneSubmitStatus === "saving"}
                onClick={() => void handleZoneSave()}
                type="button"
              >
                {zoneSubmitStatus === "saving" ? "Saving zone..." : "Save zone name"}
              </Button>
            </div>
          </section>
        ) : (
          <>
            {isVehicleOrFleetTagFlow ? (
              <section className={SECTION_CLASS}>
                <h3 className="font-semibold">
                  {bilingualLabel(
                    "Choose your reporting language",
                    "chooseLanguage",
                    preferredLanguage
                  )}
                </h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Select the language you are most comfortable with. The report labels
                  switch to bilingual mode and the same language is attached to this
                  incident and voice note metadata.
                </p>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  {DRIVER_LANGUAGE_OPTIONS.map((option) => {
                    const isSelected = option.value === preferredLanguage

                    return (
                      <button
                        aria-label={option.label}
                        className={
                          isSelected
                            ? "rounded-2xl border border-primary/40 bg-primary/5 px-4 py-3 text-left"
                            : "rounded-2xl border border-border/70 bg-background px-4 py-3 text-left"
                        }
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setPreferredLanguage(option.value)
                          setTemplateDraft((currentDraft) =>
                            currentDraft
                              ? {
                                  ...currentDraft,
                                  form: {
                                    ...currentDraft.form,
                                    values: {
                                      ...currentDraft.form.values,
                                    },
                                  },
                                }
                              : currentDraft
                          )
                        }}
                      >
                        <div className="text-sm font-semibold">{option.nativeLabel}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {option.helper}
                        </div>
                      </button>
                    )
                  })}
                </div>
                {preferredLanguage ? (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Reporting language selected:{" "}
                    {
                      DRIVER_LANGUAGE_OPTIONS.find((option) => option.value === preferredLanguage)
                        ?.helper
                    }
                  </p>
                ) : null}
              </section>
            ) : null}

            <section className={SECTION_CLASS}>
              <div className={`${BANNER_BASE} mt-5 border-border/70 bg-background/70`}>
                <strong className="block font-medium">{subjectSummary.title}</strong>
                <p className="mt-1 text-muted-foreground">{subjectSummary.description}</p>
              </div>

              {reportingTemplate && templateForm ? (
                <div className="mt-5 space-y-5">
                  {reportingTemplate.actions.length > 1 ? (
                    <div>
                      <h3 className="font-semibold">
                        {isPlantationsTemplate ? "Plantation actions" : "Choose an action"}
                      </h3>
                      {isPlantationsTemplate ? (
                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                          {reportingTemplate.actions.map((action) => {
                            const selected = resolvedTemplateAction?.id === action.id
                            const isPanicAction = action.id === "panic_alert"

                            return (
                              <button
                                aria-label={isPanicAction ? "Panic Alert" : "Incident Report"}
                                className={`rounded-3xl border p-4 text-left transition ${
                                  isPanicAction
                                    ? selected
                                      ? "border-rose-500 bg-rose-100 text-rose-950"
                                      : "border-rose-300/80 bg-rose-50 text-rose-900 hover:border-rose-400"
                                    : selected
                                      ? "border-primary bg-primary/10 text-foreground"
                                      : "border-border/70 bg-background/70 text-muted-foreground hover:border-primary/40"
                                }`}
                                disabled={submitStatus === "submitting" && isPanicAction}
                                key={action.id}
                                onClick={() =>
                                  isPanicAction
                                    ? void handlePlantationPanicAlert()
                                    : handleTemplateActionChange(action.id)
                                }
                                type="button"
                              >
                                <span className="block text-sm font-semibold">
                                  {action.label}
                                </span>
                                <span className="mt-2 block text-sm leading-6">
                                  {action.description}
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                          {reportingTemplate.actions.map((action) => {
                            const selected = resolvedTemplateAction?.id === action.id

                            return (
                              <button
                                className={`rounded-3xl border p-4 text-left transition ${
                                  selected
                                    ? "border-primary bg-primary/10 text-foreground"
                                    : "border-border/70 bg-background/70 text-muted-foreground hover:border-primary/40"
                                }`}
                                key={action.id}
                                onClick={() => handleTemplateActionChange(action.id)}
                                type="button"
                              >
                                <span className="block text-sm font-semibold">
                                  {action.label}
                                </span>
                                <span className="mt-2 block text-sm leading-6">
                                  {action.description}
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      )}
                      {isPlantationsTemplate ? (
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                          <InfoStat
                            label="Plantation"
                            value={resolvedSiteContext || "Plantation not assigned"}
                          />
                          <InfoStat
                            label="Zone"
                            value={resolvedZoneName || "Zone name required"}
                          />
                          <InfoStat
                            label="Reporting language"
                            value={
                              plantationsLocalLanguageLabel
                                ? `English + ${plantationsLocalLanguageLabel}`
                                : "English"
                            }
                          />
                        </div>
                      ) : null}
                      {isPlantationsTemplate ? (
                        <div className={`${BANNER_BASE} mt-4 border-emerald-300/70 bg-emerald-100/60 text-emerald-950`}>
                          {plantationsLocalLanguageLabel
                            ? `GPS is captured automatically when available and Panic Alert stays available in English + ${plantationsLocalLanguageLabel}.`
                            : "GPS is captured automatically when available and is attached to Panic Alert first."}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {resolvedTemplateAction && !isPlantationsTemplate ? (
                    <div className={`${BANNER_BASE} border-border/70 bg-background/70`}>
                      <strong className="block font-medium">
                        {resolvedTemplateAction.label}
                      </strong>
                      <p className="mt-1 text-muted-foreground">
                        {resolvedTemplateAction.description}
                      </p>
                    </div>
                  ) : null}

                  {resolvedTemplateAction?.id !== "panic_alert" ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      {visibleTemplateFields.map((field) => {
                        const shouldSpanFullWidth =
                          field.kind === "choice" || field.kind === "textarea"

                        return (
                          <div
                            className={shouldSpanFullWidth ? "md:col-span-2" : ""}
                            key={field.id}
                          >
                            <TemplateField
                              field={field}
                              language={preferredLanguage}
                              onChange={(value) => updateTemplateForm(field, value)}
                              required={Boolean(
                                resolvedTemplateAction?.requiredFieldIds.includes(field.id)
                              )}
                              value={templateForm.values[field.id]}
                            />
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <ChoiceField
                      label={bilingualLabel("Report path", "reportPath", preferredLanguage)}
                      name="reportKind"
                      onChange={(value) =>
                        updateForm("reportKind", value as ReportFormState["reportKind"])
                      }
                      options={[
                        { label: "Incident", value: "incident" },
                        { label: "Behavior", value: "behavior" },
                      ]}
                      value={form.reportKind}
                    />
                    <ChoiceField
                      label={bilingualLabel("Severity", "severity", preferredLanguage)}
                      name="severity"
                      onChange={(value) => updateForm("severity", value as Severity)}
                      options={REPORT_SEVERITIES}
                      value={form.severity}
                    />
                  </div>

                  {form.reportKind === "incident" ? (
                    <div className="mt-5 grid gap-4 md:grid-cols-2">
                      <SelectField
                        label="Incident type"
                        name="incidentType"
                        onChange={(value) =>
                          updateForm("incidentType", value as IncidentType)
                        }
                        options={REPORT_INCIDENT_TYPES}
                        value={form.incidentType}
                      />
                      <SelectField
                        label="Injury status"
                        name="injuryStatus"
                        onChange={(value) => updateForm("injuryStatus", value)}
                        options={[
                          { label: "No known injuries", value: "none-known" },
                          { label: "Possible injuries", value: "possible" },
                          { label: "Serious injuries", value: "serious" },
                          { label: "Unknown", value: "unknown" },
                        ]}
                        value={form.injuryStatus}
                      />
                    </div>
                  ) : (
                    <div className="mt-5">
                      <SelectField
                        label="Behavior type"
                        name="behaviorType"
                        onChange={(value) =>
                          updateForm("behaviorType", value as BehaviorType)
                        }
                        options={REPORT_BEHAVIOR_TYPES}
                        value={form.behaviorType}
                      />
                    </div>
                  )}

                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <SelectField
                      label="People involved"
                      name="peopleInvolved"
                      onChange={(value) => updateForm("peopleInvolved", value)}
                      options={[
                        { label: "Select", value: "" },
                        { label: "1", value: "1" },
                        { label: "2", value: "2" },
                        { label: "3", value: "3" },
                        { label: "4", value: "4" },
                        { label: "5", value: "5" },
                        { label: "6", value: "6" },
                        { label: "7", value: "7" },
                        { label: "8", value: "8" },
                      ]}
                      value={form.peopleInvolved}
                    />
                    <SelectField
                      label="Estimated delay"
                      name="delay"
                      onChange={(value) => updateForm("delay", value)}
                      options={[
                        { label: "Select if known", value: "" },
                        { label: "< 5 minutes", value: "< 5 minutes" },
                        { label: "5-15 minutes", value: "5-15 minutes" },
                        { label: "15-30 minutes", value: "15-30 minutes" },
                        { label: "30-60 minutes", value: "30-60 minutes" },
                        { label: "1-2 hours", value: "1-2 hours" },
                        { label: "> 2 hours", value: "> 2 hours" },
                      ]}
                      value={form.delay}
                    />
                  </div>

                  <div className="mt-5 grid gap-4 md:grid-cols-3">
                    <TextField
                      label={bilingualLabel("Your name", "yourName", preferredLanguage)}
                      name="reporterName"
                      onChange={(value) => updateForm("reporterName", value)}
                      type="text"
                      value={form.reporterName}
                    />
                    <TextField
                      label={bilingualLabel("Your phone", "yourPhone", preferredLanguage)}
                      name="reporterPhone"
                      onChange={(value) => updateForm("reporterPhone", value)}
                      type="tel"
                      value={form.reporterPhone}
                    />
                    <TextField
                      label={bilingualLabel("Your email", "yourEmail", preferredLanguage)}
                      name="reporterEmail"
                      onChange={(value) => updateForm("reporterEmail", value)}
                      type="email"
                      value={form.reporterEmail}
                    />
                  </div>

                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <TextField
                      label={bilingualLabel(
                        "Location details",
                        "locationDetails",
                        preferredLanguage
                      )}
                      name="locationDescription"
                      onChange={(value) => updateForm("locationDescription", value)}
                      type="text"
                      value={form.locationDescription}
                    />
                    <TextAreaField
                      label={bilingualLabel("Scene details", "sceneDetails", preferredLanguage)}
                      name="description"
                      onChange={(value) => updateForm("description", value)}
                      value={form.description}
                    />
                  </div>
                </>
              )}

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className={LABEL_CLASS}>
                    {bilingualLabel("GPS status", "gpsStatus", preferredLanguage)}
                  </span>
                  <div
                    className={`${BANNER_BASE} mt-2 ${
                      gpsStatus === "ready"
                        ? "border-emerald-300/70 bg-emerald-100/70 text-emerald-950"
                        : gpsStatus === "error"
                          ? "border-amber-300/70 bg-amber-100/70 text-amber-950"
                          : "border-border/70 bg-background/70"
                    }`}
                  >
                    {gpsMessage}
                  </div>
                  <div className="mt-3">
                    <Button
                      onClick={() => requestGpsLocation()}
                      type="button"
                      variant="outline"
                    >
                      Retry GPS
                    </Button>
                  </div>
                </label>

                {reportingTemplate ? (
                  <div className={`${BANNER_BASE} border-amber-300/70 bg-amber-100/60 text-amber-950`}>
                    {templateSubmitsDirectly
                      ? "No OTP is required. Submit once and the report is sent immediately."
                      : "No OTP is required before bystander submission. Review the template, then confirm once to send the report."}
                  </div>
                ) : null}
              </div>
            </section>

            <section className={SECTION_CLASS}>
              <h3 className="font-semibold">Evidence</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Add photos or a voice note if they help explain what happened.
              </p>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className={LABEL_CLASS}>
                    {bilingualLabel(
                      "Incident photos",
                      "incidentPhotos",
                      preferredLanguage
                    )}
                  </span>
                  <input
                    accept="image/jpg,image/jpeg,image/png,image/webp,image/heic,image/heif"
                    className={INPUT_CLASS}
                    multiple
                    name="photos"
                    onChange={handlePhotoSelection}
                    type="file"
                  />
                  <p className={HELP_CLASS}>
                    Up to {reportingTemplate?.media.maxPhotos ?? 4} images, 6 MB each, 24 MB total.
                  </p>
                </label>

                {reportingTemplate?.media.allowVoiceNote !== false ? (
                  <label className="block">
                    <span className={LABEL_CLASS}>
                      {bilingualLabel("Voice note", "voiceNote", preferredLanguage)}
                    </span>
                    <input
                      accept="audio/mpeg,audio/mp3,audio/mp4,audio/m4a,audio/x-m4a,audio/x-mp4a,audio/mp4a-latm,audio/aac,audio/webm,audio/wav,audio/x-wav,audio/ogg,audio/mpga,audio/3gp,audio/3gpp"
                      className={INPUT_CLASS}
                      name="voiceNote"
                      onClick={(event) => {
                        event.currentTarget.value = ""
                      }}
                      onChange={handleVoiceSelection}
                      type="file"
                    />
                    <p className={HELP_CLASS}>
                      Optional single voice note to provide extra context.
                    </p>
                  </label>
                ) : null}
              </div>

              {selectedPhotos.length > 0 ? (
                <div className="mt-5 rounded-3xl border border-border/70 bg-background/70 p-4">
                  <h4 className="font-medium">Selected photos</h4>
                  <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                    {selectedPhotos.map((file) => (
                      <li key={`${file.name}-${file.size}`}>{file.name}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {selectedVoiceNote ? (
                <div className="mt-4 rounded-3xl border border-emerald-300/70 bg-emerald-100/60 p-4 text-sm text-emerald-950">
                  <div className="font-medium">Voice note attached</div>
                  <p className="mt-1">
                    {selectedVoiceNote.name} ({(selectedVoiceNote.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                </div>
              ) : null}
            </section>
          </>
        )}

        {formError ? (
          <div className={`${BANNER_BASE} border-rose-300/70 bg-rose-100/70 text-rose-950`}>
            {formError}
          </div>
        ) : null}

        {!reportingUiBlocked && reportingTemplate && resolvedTemplateAction && templateForm ? (
          isPlantationsTemplate && resolvedTemplateAction.id === "panic_alert" ? null : !templateSubmitsDirectly && templateConfirmationVisible ? (
            <div className={`${BANNER_BASE} border-emerald-300/70 bg-emerald-100/60 text-emerald-950`}>
              <strong className="block font-medium">
                {resolvedTemplateConfirmation?.title}
              </strong>
              <p className="mt-2">{resolvedTemplateConfirmation?.body}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button
                  disabled={submitStatus === "submitting"}
                  onClick={() => void handleTemplateConfirm()}
                  type="button"
                >
                  {submitStatus === "submitting"
                    ? submitMessage
                    : resolvedTemplateConfirmation?.actionLabel}
                </Button>
                <Button
                  onClick={() => setTemplateConfirmationVisible(false)}
                  type="button"
                  variant="outline"
                >
                  Keep editing
                </Button>
              </div>
            </div>
          ) : (
              <Button disabled={submitStatus === "submitting"} type="submit">
              {submitStatus === "submitting"
                ? submitMessage
                : (resolvedTemplateAction.id === "report_incident"
                  ? bilingualLabel(
                    resolvedTemplateSubmitLabel,
                    "submitReport",
                    preferredLanguage
                  )
                  : resolvedTemplateSubmitLabel)}
            </Button>
          )
        ) : !reportingUiBlocked ? (
          <Button disabled={submitStatus === "submitting"} type="submit">
            {submitStatus === "submitting"
              ? submitMessage
              : bilingualLabel("Submit report", "submitReport", preferredLanguage)}
          </Button>
        ) : null}
      </form>

      <aside className="space-y-5">
        {isBootstrapLookupPending ? (
          <PublicSidebarSectionSkeleton
            rows={5}
            testId="public-report-sidebar-skeleton"
          />
        ) : (
          <section className={SECTION_CLASS}>
            <h3 className="font-semibold">Current lookup snapshot</h3>
            <div className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
              <p>Tag type: {lookupData?.tagType ?? "unknown"}</p>
              <p>Activation: {lookupData?.activationState ?? "unresolved"}</p>
              <p>Incidents: {lookupData?.incidentCount ?? 0}</p>
              <p>Template: {reportingTemplate?.displayName ?? "Standard reporting flow"}</p>
              {isPlantationsLookup ? (
                <>
                  <p>Plantation: {resolvedSiteContext || "Plantation not assigned"}</p>
                  <p>Zone: {resolvedZoneName || "Zone name required"}</p>
                </>
              ) : null}
            </div>
          </section>
        )}
      </aside>
    </section>
  )
}

function InfoStat(props: { label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/70 p-4">
      <dt className="text-xs tracking-[0.2em] text-muted-foreground uppercase">
        {props.label}
      </dt>
      <dd className="mt-2 text-sm font-medium text-foreground">{props.value}</dd>
    </div>
  )
}

function SuccessStat(props: { label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/70 p-4">
      <dt className="text-xs tracking-[0.2em] text-muted-foreground uppercase">
        {props.label}
      </dt>
      <dd className="mt-2 text-base font-semibold text-foreground">
        {props.value}
      </dd>
    </div>
  )
}

function TextField(props: {
  label: string
  name: string
  onChange: (value: string) => void
  type: string
  value: string
}) {
  return (
    <label className="block">
      <span className={LABEL_CLASS}>{props.label}</span>
      <input
        className={INPUT_CLASS}
        name={props.name}
        onChange={(event) => props.onChange(event.target.value)}
        type={props.type}
        value={props.value}
      />
    </label>
  )
}

function TextAreaField(props: {
  label: string
  name: string
  onChange: (value: string) => void
  value: string
}) {
  return (
    <label className="mt-5 block">
      <span className={LABEL_CLASS}>{props.label}</span>
      <textarea
        className={TEXTAREA_CLASS}
        name={props.name}
        onChange={(event) => props.onChange(event.target.value)}
        value={props.value}
      />
    </label>
  )
}

function SelectField(props: {
  label: string
  name: string
  onChange: (value: string) => void
  options: Array<{ label: string; value: string }>
  value: string
}) {
  return (
    <label className="block">
      <span className={LABEL_CLASS}>{props.label}</span>
      <select
        className={INPUT_CLASS}
        name={props.name}
        onChange={(event) => props.onChange(event.target.value)}
        value={props.value}
      >
        {props.options.map((option) => (
          <option key={option.value || option.label} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function ChoiceField(props: {
  label: string
  name: string
  onChange: (value: string) => void
  options: Array<{ label: string; value: string }>
  value: string
}) {
  return (
    <fieldset>
      <legend className={LABEL_CLASS}>{props.label}</legend>
      <div className="mt-3 flex flex-wrap gap-3">
        {props.options.map((option) => {
          const checked = props.value === option.value

          return (
            <label
              key={option.value}
              className={`cursor-pointer rounded-3xl border px-4 py-3 text-sm transition ${
                checked
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border bg-background/70 text-muted-foreground hover:border-primary/40"
              }`}
            >
              <input
                checked={checked}
                className="sr-only"
                name={props.name}
                onChange={(event) => props.onChange(event.target.value)}
                type="radio"
                value={option.value}
              />
              {option.label}
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

function TemplateField(props: {
  field: SafetyQrReportingFieldDefinition
  language: DriverLanguage | null
  onChange: (value: TemplateReportFieldValue) => void
  required: boolean
  value: TemplateReportFieldValue | undefined
}) {
  const requiredLabel = props.required ? " *" : ""
  const localizedLabel = bilingualTemplateFieldLabel(props.field, props.language)
  const localizedHelperText = bilingualTemplateHelperText(
    props.field,
    props.language
  )

  if (props.field.kind === "choice") {
    return (
      <ChoiceField
        label={`${localizedLabel}${requiredLabel}`}
        name={props.field.id}
        onChange={props.onChange}
        options={(props.field.options ?? []).map((option) => ({
          ...option,
          label: bilingualTemplateOptionLabel(props.field, option, props.language),
        }))}
        value={typeof props.value === "string" ? props.value : ""}
      />
    )
  }

  if (props.field.kind === "select") {
    return (
      <label className="block">
        <span className={LABEL_CLASS}>
          {localizedLabel}
          {requiredLabel}
        </span>
        <select
          className={INPUT_CLASS}
          name={props.field.id}
          onChange={(event) => props.onChange(event.target.value)}
          value={typeof props.value === "string" ? props.value : ""}
        >
          {(props.field.options ?? []).map((option) => (
            <option key={option.value || option.label} value={option.value}>
              {bilingualTemplateOptionLabel(props.field, option, props.language)}
            </option>
          ))}
        </select>
        {localizedHelperText ? (
          <p className={HELP_CLASS}>{localizedHelperText}</p>
        ) : null}
      </label>
    )
  }

  if (props.field.kind === "textarea") {
    return (
      <label className="block">
        <span className={LABEL_CLASS}>
          {localizedLabel}
          {requiredLabel}
        </span>
        <textarea
          className={TEXTAREA_CLASS}
          name={props.field.id}
          onChange={(event) => props.onChange(event.target.value)}
          value={typeof props.value === "string" ? props.value : ""}
        />
        {localizedHelperText ? (
          <p className={HELP_CLASS}>{localizedHelperText}</p>
        ) : null}
      </label>
    )
  }

  if (props.field.kind === "toggle") {
    return (
      <label className="flex items-start gap-3 rounded-3xl border border-border/70 bg-background/70 p-4">
        <input
          checked={props.value === true}
          className="mt-1 h-4 w-4 rounded border-border"
          name={props.field.id}
          onChange={(event) => props.onChange(event.target.checked)}
          type="checkbox"
        />
        <span>
          <span className="block text-sm font-medium text-foreground">
            {localizedLabel}
          </span>
          {localizedHelperText ? (
            <span className="mt-1 block text-xs leading-5 text-muted-foreground">
              {localizedHelperText}
            </span>
          ) : null}
        </span>
      </label>
    )
  }

  return (
    <TextField
      label={`${localizedLabel}${requiredLabel}`}
      name={props.field.id}
      onChange={props.onChange}
      type={props.field.kind}
      value={typeof props.value === "string" ? props.value : ""}
    />
  )
}
